import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.2, 0.8, 0.2, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Cta = ({ label, link, variant = "default", testId, arrow = false, className = "" }) => {
  if (!label || !link) return null;
  const inner = (
    <>
      {label}
      {arrow && <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />}
    </>
  );
  return (
    <Button variant={variant} size="lg" className={`rounded-full px-6 ${className}`} asChild data-testid={testId}>
      {link.startsWith("http") ? <a href={link}>{inner}</a> : <Link to={link}>{inner}</Link>}
    </Button>
  );
};

export const SectionHeading = ({ eyebrow, title, subtitle, align = "left", dark = false, testId }) => (
  <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`} data-testid={testId}>
    {eyebrow && <p className="eyebrow">{eyebrow}</p>}
    {title && (
      <h2 className={`mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl ${dark ? "text-ink-foreground" : ""}`}>
        {title}
      </h2>
    )}
    {subtitle && <p className="mt-4 text-base leading-relaxed text-muted-foreground">{subtitle}</p>}
  </div>
);

/** Compacte slider-toggle: gelijke segmenten, de knop schuift onder het actieve label. */
export const SegmentToggle = ({ options, value, onChange, testIdPrefix, dark = false }) => (
  <div
    className={`relative inline-grid rounded-full border p-0.5 ${dark ? "border-white/15 bg-white/5" : "border-border bg-card"}`}
    style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    role="tablist"
  >
    {options.map((o) => {
      const active = o.id === value;
      return (
        <button
          key={o.id}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(o.id)}
          className={`relative min-w-[84px] rounded-full px-3 py-1.5 font-display text-xs font-semibold tracking-tight transition-colors sm:min-w-[96px] sm:text-sm ${
            active ? (dark ? "text-ink" : "text-primary-foreground") : dark ? "text-ink-foreground/70 hover:text-ink-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`${testIdPrefix}-${o.id}`}
        >
          {active && (
            <motion.span
              layoutId={`${testIdPrefix}-indicator`}
              className={`absolute inset-0 rounded-full shadow-sm ${dark ? "bg-ink-foreground" : "bg-primary"}`}
              transition={{ type: "spring", stiffness: 480, damping: 36 }}
            />
          )}
          <span className="relative z-10">{o.label}</span>
        </button>
      );
    })}
  </div>
);
